import {
    DossierDetailType,
    DossierStatusEnum,
    InstitutionalAffiliationType,
    OverViewDossierType,
    Session,
} from "../../../../types";
import { Box, Typography } from "@mui/material";
import LinearProgressWithLabel from "../../../UIElements/LinearProgressWithLabel";
import CustomButton from "../../../UIElements/CustomButton";
import OverViewDossier from "./OverViewDossier";
import { getColorByPercent, buildInstructionInfos } from "../utils";
import Dossier from "../Dossier";
import { useContext } from "react";
import { LayoutBarContext } from "../../../../contexts";
import { useLocalStorage } from "../../../../hooks";
import {
    sessionStorageKey,
    settingStorageKey,
    daysBeforeDeadlineKey,
    processingStepThresholdKey,
    remainingDaysBeforeDeadlineKey,
} from "../../../../consts";

type OverViewDossierInstructorProps = {
    dossierDetail: DossierDetailType;
    linearProgressValue: number;
    disablePaymentBtn?: boolean;
    isPayed?: boolean;
    institutionalAffiliation?: InstitutionalAffiliationType;
};

export default function OverViewDossierInstructor({
    dossierDetail,
    linearProgressValue,
    disablePaymentBtn,
    isPayed,
    institutionalAffiliation,
}: OverViewDossierInstructorProps) {
    const { updateContent } = useContext(LayoutBarContext);

    const { storedValue: session } =
        useLocalStorage<Session>(sessionStorageKey);

    const {
        [settingStorageKey]: [...settings],
    } = session;

    const daysBeforeDeadline = Number(
        (settings.find((s) => s.key === daysBeforeDeadlineKey) || {}).value
    );
    const processingStepThreshold = Number(
        (settings.find((s) => s.key === processingStepThresholdKey) || {}).value
    );

    const remainingNbDaysToInstructThreshold = Number(
        (settings.find((s) => s.key === remainingDaysBeforeDeadlineKey) || {})
            .value
    );

    // ########## instructSpace : overview dossier infos to instruct
    const buildOverviewDossierInfosToInstruct = (
        dossierDetail: DossierDetailType
    ): OverViewDossierType => {
        const { instructionInfos, remainingNbDaysToInstruct } =
            buildInstructionInfos(dossierDetail, daysBeforeDeadline);

        const dossierInIstruction =
            dossierDetail?.dossierStatus?.code &&
            ![
                DossierStatusEnum.FINALISE,
                DossierStatusEnum.ATTENTE_DECISION,
            ].includes(dossierDetail?.dossierStatus?.code as any);

        return {
            alertText:
                remainingNbDaysToInstruct <=
                    remainingNbDaysToInstructThreshold && dossierInIstruction
                    ? `Il ne vous reste que ${remainingNbDaysToInstruct} jours pour instruire ce dossier`
                    : "",
            status: dossierDetail.dossierStatus.label,
            statusCode: dossierDetail.dossierStatus.code,
            subTitle: `Dossier de qualification pour ${dossierDetail.referenciel.name}`,
            title: dossierDetail.establishment.companyName,
            dossierId: dossierDetail?.id,
            disablePaymentBtn,
            isPayed,
            institutionalAffiliation,
            content: (
                <>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            gap: "0.5rem",
                            alignSelf: "stretch",
                        }}
                    >
                        <Typography
                            sx={{
                                color: "#555A5D",

                                fontFamily: "Roboto",
                                fontSize: "0.875rem",
                                fontStyle: "normal",
                                fontWeight: 400,
                                lineHeight: "normal",
                                alignSelf: "stretch",
                            }}
                        >
                            Instruction des engagements
                        </Typography>
                        <LinearProgressWithLabel
                            value={linearProgressValue}
                            classes={{
                                rootsx: {
                                    width: "100%",
                                },
                                linearProgressSx: {
                                    background: "#E3E6E8",
                                    ".MuiLinearProgress-bar": {
                                        backgroundColor:
                                            getColorByPercent(
                                                linearProgressValue
                                            ),
                                    },
                                },
                            }}
                        />
                    </Box>
                    {instructionInfos ? (
                        <>
                            <Box
                                sx={{
                                    display: "flex",
                                    // flexDirection: "column",
                                    alignItems: "flex-start",
                                    gap: "1rem",
                                    alignSelf: "stretch",
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "flex-start",
                                        gap: "0.5rem",
                                        alignSelf: "stretch",
                                        flex: "1 0 0",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: "#555A5D",

                                            fontFamily: "Roboto",
                                            fontSize: "0.875rem",
                                            fontStyle: "normal",
                                            fontWeight: 400,
                                            lineHeight: "normal",
                                            alignSelf: "stretch",
                                        }}
                                    >
                                        Affecté le
                                    </Typography>
                                    <Typography
                                        variant="h1"
                                        sx={{
                                            color: "#555A5D",

                                            fontFamily: "Roboto",
                                            fontSize: "1rem",
                                            fontStyle: "normal",
                                            fontWeight: 500,
                                            lineHeight: "normal",
                                            alignSelf: "stretch",
                                        }}
                                    >
                                        <b>{instructionInfos.assignedDate}</b>
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "flex-start",
                                        gap: "0.5rem",
                                        alignSelf: "stretch",
                                        flex: "1 0 0",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: "#555A5D",

                                            fontFamily: "Roboto",
                                            fontSize: "0.875rem",
                                            fontStyle: "normal",
                                            fontWeight: 400,
                                            lineHeight: "normal",
                                            alignSelf: "stretch",
                                        }}
                                    >
                                        Instruction à fournir avant le
                                    </Typography>
                                    <Typography
                                        variant="h1"
                                        sx={{
                                            color: "#555A5D",

                                            fontFamily: "Roboto",
                                            fontSize: "1rem",
                                            fontStyle: "normal",
                                            fontWeight: 500,
                                            lineHeight: "normal",
                                            alignSelf: "stretch",
                                        }}
                                    >
                                        <b>
                                            {
                                                instructionInfos.instructionDeadline
                                            }
                                        </b>
                                    </Typography>
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    // flexDirection: "column",
                                    alignItems: "flex-start",
                                    gap: "1rem",
                                    alignSelf: "stretch",
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "flex-start",
                                        gap: "0.5rem",
                                        alignSelf: "stretch",
                                        flex: "1 0 0",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: "#555A5D",

                                            fontFamily: "Roboto",
                                            fontSize: "0.875rem",
                                            fontStyle: "normal",
                                            fontWeight: 400,
                                            lineHeight: "normal",
                                            alignSelf: "stretch",
                                        }}
                                    >
                                        Aller/retour
                                    </Typography>
                                    <Typography
                                        variant="h1"
                                        sx={{
                                            color: "#555A5D",

                                            fontFamily: "Roboto",
                                            fontSize: "1rem",
                                            fontStyle: "normal",
                                            fontWeight: 500,
                                            lineHeight: "normal",
                                            alignSelf: "stretch",
                                        }}
                                    >
                                        <b>{`${instructionInfos.processStep}/${processingStepThreshold}`}</b>
                                    </Typography>
                                </Box>
                            </Box>
                        </>
                    ) : null}
                </>
            ),
            actions: {
                edit: (
                    <CustomButton
                        onClick={() => {
                            if (dossierDetail.id) {
                                updateContent(
                                    <Dossier dossierId={dossierDetail.id} />
                                );
                            }
                        }}
                        label="Instruire le dossier"
                        customsx={{
                            width: "100%",
                        }}
                    />
                ),
            },
        };
    };

    return (
        <OverViewDossier
            overViewDossierInfo={buildOverviewDossierInfosToInstruct(
                dossierDetail
            )}
        />
    );
}
